import { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Select } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useBulkSetFlag, useBulkDeleteProducts, ProductFlagType } from '@modules/products';
import { DoubleCheckModal } from '@shared/ui';

interface BulkActionsSelectProps {
    selectedProductIds: Set<number>;
    onActionComplete?: () => void;
    onOpenAddToSetModal?: (productIds: number[]) => void;
    onOpenAddToCategoryModal?: (productIds: number[]) => void;
}

export function BulkActionsSelect({
    selectedProductIds,
    onActionComplete,
    onOpenAddToSetModal,
    onOpenAddToCategoryModal,
}: BulkActionsSelectProps) {
    const location = useLocation();
    const isSellerAdmin = useMemo(() => {
        try {
            const raw = localStorage.getItem('luxus_admin');
            if (!raw) return false;
            const p = JSON.parse(raw) as { role?: string };
            return p.role === 'SELLER_ADMIN';
        } catch {
            return false;
        }
    }, [location.pathname]);

    const [selectedAction, setSelectedAction] = useState<string | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const bulkActionOptions = useMemo(
        () => {
            const items: { value: string; label: string }[] = [
                { value: 'hide', label: 'Скрыть' },
                { value: 'show', label: 'Показать' },
            ];
            if (isSellerAdmin) {
                items.push(
                    { value: 'out-of-stock', label: 'Установить "не в наличии"' },
                    { value: 'in-stock', label: 'Установить "в наличии"' },
                );
            }
            items.push({ value: 'add-to-set', label: 'Добавить в подборку' });
            if (isSellerAdmin) {
                items.push({ value: 'add-to-category', label: 'Добавить в категорию' });
            }
            items.push({ value: 'delete', label: 'Удалить' });
            return items;
        },
        [isSellerAdmin],
    );

    // Хуки для массовых действий
    const bulkSetHidden = useBulkSetFlag(ProductFlagType.HIDDEN);
    const bulkSetOutOfStock = useBulkSetFlag(ProductFlagType.OUT_OF_STOCK);
    const bulkDeleteProducts = useBulkDeleteProducts();

    const handleBulkAction = async (action: string) => {
        if (selectedProductIds.size === 0) {
            return;
        }

        const productIdsArray = Array.from(selectedProductIds);

        switch (action) {
            case 'hide':
                try {
                    await bulkSetHidden.mutateAsync({
                        productIds: productIdsArray,
                        value: true,
                    });
                    notifications.show({
                        title: 'Успешно',
                        message: 'Товары успешно скрыты',
                        color: 'green',
                    });
                    onActionComplete?.();
                    setTimeout(() => {
                        setSelectedAction(null);
                    }, 100);
                } catch (error) {
                    notifications.show({
                        title: 'Ошибка',
                        message: 'Ошибка при скрытии товаров',
                        color: 'red',
                    });
                    setSelectedAction(null);
                }
                break;

            case 'show':
                try {
                    await bulkSetHidden.mutateAsync({
                        productIds: productIdsArray,
                        value: false,
                    });
                    notifications.show({
                        title: 'Успешно',
                        message: 'Товары успешно показаны',
                        color: 'green',
                    });
                    onActionComplete?.();
                    setTimeout(() => {
                        setSelectedAction(null);
                    }, 100);
                } catch (error) {
                    notifications.show({
                        title: 'Ошибка',
                        message: 'Ошибка при показе товаров',
                        color: 'red',
                    });
                    setSelectedAction(null);
                }
                break;

            case 'out-of-stock':
                try {
                    await bulkSetOutOfStock.mutateAsync({
                        productIds: productIdsArray,
                        value: true,
                    });
                    notifications.show({
                        title: 'Успешно',
                        message: 'Товары помечены как не в наличии',
                        color: 'green',
                    });
                    onActionComplete?.();
                    setTimeout(() => {
                        setSelectedAction(null);
                    }, 100);
                } catch (error) {
                    notifications.show({
                        title: 'Ошибка',
                        message: 'Ошибка при установке статуса "не в наличии"',
                        color: 'red',
                    });
                    setSelectedAction(null);
                }
                break;

            case 'in-stock':
                try {
                    await bulkSetOutOfStock.mutateAsync({
                        productIds: productIdsArray,
                        value: false,
                    });
                    notifications.show({
                        title: 'Успешно',
                        message: 'Товары помечены как в наличии',
                        color: 'green',
                    });
                    onActionComplete?.();
                    setTimeout(() => {
                        setSelectedAction(null);
                    }, 100);
                } catch (error) {
                    notifications.show({
                        title: 'Ошибка',
                        message: 'Ошибка при установке статуса "в наличии"',
                        color: 'red',
                    });
                    setSelectedAction(null);
                }
                break;

            case 'delete':
                setIsDeleteModalOpen(true);
                break;

            case 'add-to-set':
                onOpenAddToSetModal?.(productIdsArray);
                setSelectedAction(null);
                return;

            case 'add-to-category':
                onOpenAddToCategoryModal?.(productIdsArray);
                setSelectedAction(null);
                return;

            default:
                setSelectedAction(null);
                return;
        }
    };

    const handleConfirmDelete = async () => {
        const productIdsArray = Array.from(selectedProductIds);

        try {
            await bulkDeleteProducts.mutateAsync({
                productIds: productIdsArray,
            });
            notifications.show({
                title: 'Успешно',
                message: 'Товары успешно удалены',
                color: 'green',
            });
            setIsDeleteModalOpen(false);
            onActionComplete?.();
            setTimeout(() => {
                setSelectedAction(null);
            }, 100);
        } catch (error) {
            console.error('Ошибка при удалении товаров:', error);
            notifications.show({
                title: 'Ошибка',
                message: 'Ошибка при удалении товаров',
                color: 'red',
            });
            setIsDeleteModalOpen(false);
            setSelectedAction(null);
        }
    };

    const handleCancelDelete = () => {
        setIsDeleteModalOpen(false);
        setSelectedAction(null);
    };

    if (selectedProductIds.size === 0) {
        return null;
    }

    return (
        <div className="selection-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontWeight: 500 }}>
                Выбрано: {selectedProductIds.size}
            </span>
            <Select
                placeholder="Выберите действие"
                value={selectedAction}
                onChange={(value) => {
                    setSelectedAction(value);
                    if (value) {
                        handleBulkAction(value);
                    }
                }}
                data={bulkActionOptions}
                style={{ minWidth: 250 }}
                clearable
            />
            <DoubleCheckModal
                isOpen={isDeleteModalOpen}
                onClose={handleCancelDelete}
                title="Удалить товары?"
                message={`Вы уверены, что хотите удалить ${selectedProductIds.size} товар(ов)?`}
                secondButtonText="Удалить"
                secondButtonAction={handleConfirmDelete}
                isLoading={bulkDeleteProducts.isPending}
            />
        </div>
    );
}

